import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { CompletionBar } from '@/components/ui/CompletionBar'
import { useAuthStore } from '@/store/authStore'
import { BasicDetailsForm } from '@/features/student/components/BasicDetailsForm'
import { DocumentsSection } from '@/features/student/components/DocumentsSection'
import { EducationSection } from '@/features/student/components/EducationSection'
import { ExperienceSection } from '@/features/student/components/ExperienceSection'
import { PreferencesForm } from '@/features/student/components/PreferencesForm'
import { SkillSection } from '@/features/student/components/SkillSection'
import { useStudentProfileQuery } from '@/features/student/hooks/useProfile'

const STEPS = [
  'Account',
  'Basic Details',
  'Education',
  'Experience',
  'Skills',
  'Preferences',
  'Documents',
  'Review & Publish',
] as const

function stepStorageKey(userId: number) {
  return `job-portal.profile-wizard-step.${userId}`
}

export function ProfileWizardPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { data: profile, isPending } = useStudentProfileQuery()
  const [step, setStep] = useState(() => {
    if (!user) return 0
    const saved = localStorage.getItem(stepStorageKey(user.id))
    return saved ? Math.min(Number(saved), STEPS.length - 1) : 0
  })

  useEffect(() => {
    if (!user) return
    localStorage.setItem(stepStorageKey(user.id), String(step))
  }, [step, user])

  if (isPending || !profile || !user) {
    return (
      <Card>
        <p className="text-muted mb-0">Loading your profile…</p>
      </Card>
    )
  }

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1))
  const goBack = () => setStep((s) => Math.max(s - 1, 0))
  const finish = () => {
    localStorage.removeItem(stepStorageKey(user.id))
    void navigate('/profile', { replace: true })
  }

  return (
    <>
      <div className="my-4 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-0">Complete Your Profile</p>
        <p className="fs-13 text-muted mb-0">
          Step {step + 1} of {STEPS.length}: {STEPS[step]}
        </p>
      </div>

      <Card className="mb-3">
        <CompletionBar percent={profile.profileCompletionPercent} label="Profile completeness" />
      </Card>

      <ul className="nav nav-pills mb-3 flex-wrap">
        {STEPS.map((label, idx) => (
          <li className="nav-item" key={label}>
            <button
              type="button"
              className={`nav-link ${idx === step ? 'active' : ''}`.trim()}
              onClick={() => setStep(idx)}
            >
              {idx + 1}. {label}
            </button>
          </li>
        ))}
      </ul>

      <Card>
        {step === 0 && (
          <div>
            <p className="fw-medium">Account created</p>
            <p className="text-muted mb-0">
              {user.name} · {user.email}
            </p>
          </div>
        )}
        {step === 1 && <BasicDetailsForm profile={profile} onSaved={goNext} />}
        {step === 2 && <EducationSection items={profile.educations} />}
        {step === 3 && <ExperienceSection items={profile.experiences} />}
        {step === 4 && <SkillSection items={profile.skills} />}
        {step === 5 && <PreferencesForm profile={profile} onSaved={goNext} />}
        {step === 6 && <DocumentsSection profile={profile} />}
        {step === 7 && (
          <div>
            <p className="fw-medium">You're all set!</p>
            <p className="text-muted">
              Your profile is {profile.profileCompletionPercent}% complete. You can keep adding
              details any time from "My Profile".
            </p>
            <Button onClick={finish}>Go to My Profile</Button>
          </div>
        )}
      </Card>

      {step < STEPS.length - 1 && (
        <div className="d-flex justify-content-between mt-3">
          <Button variant="ghost" onClick={goBack} disabled={step === 0}>
            Back
          </Button>
          <Button onClick={goNext}>Next</Button>
        </div>
      )}
    </>
  )
}
